import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import HeaderSearch from './Search';
import HeaderNotifications from './Notifications';
import HeaderMessages from './Messages';
import {
  AdminWorkspaceState,
  useAdminWorkspace
} from 'src/workspaces/AdminWorkspaceProvider';
import { useCompanyProfileWorkspaceScope } from 'src/workspaces/CompanyProfileWorkspaceScope';
import { resolveCompanyLogoUrl } from 'src/workspaces/companyLogo';

export interface HeaderCompanyIdentity {
  status: 'ready' | 'loading' | 'none' | 'unavailable';
  name: string;
  logo?: string;
}

export const resolveHeaderCompanyIdentity = (
  state: AdminWorkspaceState,
  company?: { name?: string | null; logo?: string | null } | null
): HeaderCompanyIdentity => {
  const name = company?.name?.trim();
  if (name) {
    return {
      status: 'ready',
      name,
      logo: resolveCompanyLogoUrl(company?.logo)
    };
  }
  if (state.kind === 'company' && state.status === 'loading') {
    return { status: 'loading', name: 'Loading company…' };
  }
  if (state.kind === 'platform') {
    return { status: 'none', name: 'No company selected' };
  }
  return { status: 'unavailable', name: 'Company unavailable' };
};

export const shouldShowGlobalNotifications = (
  state: AdminWorkspaceState
): boolean => state.kind === 'platform' || (state.kind === 'company' && state.status === 'ready');

function HeaderButtons({ companyProfilePath }: { companyProfilePath?: string | null }) {
  const workspaceState = useAdminWorkspace();
  const companyScope = useCompanyProfileWorkspaceScope();
  const companyIdentity = resolveHeaderCompanyIdentity(
    workspaceState,
    companyScope?.company
  );
  const [brokenLogo, setBrokenLogo] = useState<string | null>(null);
  const showLogo =
    companyIdentity.status === 'ready' &&
    companyIdentity.logo &&
    companyIdentity.logo !== brokenLogo;
  const environment = process.env.REACT_APP_MODE?.trim().toUpperCase() || 'UNKNOWN';

  const companyBadge = (
      <div
        title={`Active company: ${companyIdentity.name}. Environment: ${environment}`}
        aria-label={`Active company: ${companyIdentity.name}. Environment: ${environment}`}
        className="flex shrink-0 max-w-[330px] items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.04] px-2 min-[1700px]:px-3 py-2 text-white/[0.88] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
      >
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-white/[0.06]">
          {showLogo ? (
            <img
              src={companyIdentity.logo}
              alt={`${companyIdentity.name} logo`}
              onError={() => setBrokenLogo(companyIdentity.logo!)}
              className="h-full w-full object-contain"
            />
          ) : (
            <Building2
              size={16}
              aria-hidden="true"
              className={
                companyIdentity.status === 'loading'
                  ? 'animate-pulse text-white/50'
                  : 'text-[#e6cc8a]'
              }
            />
          )}
        </span>
        <span className="hidden min-[1700px]:block min-w-0 flex-1 leading-tight">
          <span className="block text-[9px] font-semibold uppercase tracking-[0.16em] text-white/45">
            Company
          </span>
          <span
            title={companyIdentity.name}
            className="block max-w-[170px] truncate text-[13px] font-semibold text-white"
          >
            {companyIdentity.name}
          </span>
        </span>
        <span className="hidden min-[1700px]:inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#1a1d25] px-2 py-1 text-[9px] font-semibold tracking-[0.12em] text-[#e6cc8a]">
          <span className="inline-flex h-2 w-2 rounded-full bg-[#8be36f] shadow-[0_0_10px_rgba(139,227,111,0.65)]" />
          {environment}
        </span>
      </div>
  );

  return (
    <div className="flex items-center gap-2.5">
      {companyProfilePath && companyIdentity.status === 'ready' ? (
        <Link to={companyProfilePath} className="flex shrink-0 rounded-2xl" aria-label={`Company Profile: ${companyIdentity.name}`}>
          {companyBadge}
        </Link>
      ) : companyBadge}

      <div className="hidden sm:flex items-center gap-2 border-l border-white/10 pl-2">
        <HeaderSearch />
        <HeaderMessages />
        {shouldShowGlobalNotifications(workspaceState) && (
          <HeaderNotifications />
        )}
      </div>
    </div>
  );
}

export default HeaderButtons;
